import { Divider, Flex, Heading, HStack } from '@chakra-ui/react';
import { keyframes } from '@emotion/react';
import { SlideType } from '@llx/common';
import { Slide } from '@llx/common';
import { Asset } from '@llx/common';
import { useEffect, useMemo, useState } from 'react';
import { fadeInLeft } from 'react-animations';
import { Trans, useTranslation } from 'react-i18next';

import liveMeetingBackground from '@/assets/images/liveMeetingBackground.svg?url';
import AssetModal from '@/components/AssetModal/AssetModal';
import { useLoggedInUserAuthContext } from '@/components/AuthTokenContextProvider/UserAuthContext';
import { UseSlideVariationReturnType } from '@/components/CaseSlide/hooks/useSlideVariation';
import AnamneseItem from '@/components/CaseSlide/items/AnamneseItem';
import { useSlideShowStore } from '@/components/CaseSlide/SlideShowSlide/slideShowData.store';
import { getAnamneseItemFromCase } from '@/features/caseCluster/helpers/getAnamneseItem';
import MeetingTestingModeWarning from '@/pages/caseCluster/MeetingTestingModeWarning';
import TopRightItem from '@/pages/caseCluster/TopRightItem';
import { getAssetListByRefOrThrow } from '@/pages/caseCluster/utils/getAssetByRef';
import { getIndexFirstSlideNotAnamnese } from '@/pages/caseCluster/utils/getIndexFirstSlideNotAnamnese';
import Empty from '@/ui/Empty/Empty';
import { SimpleContentModal } from '@/ui/SimpleContentModal';
import { createPinchingAnimationMeetingUniqueKey } from '@/utils/createPinchingAssetCookieKey';

import { CaseExplorerClientProps } from './CaseExplorerClient';
import CaseExplorerLeftSidebar from '../CaseExplorerLeftSidebar/CaseExplorerLeftSidebar';
import CaseSlide from '../CaseSlide/CaseSlide';
import { useSlideTitle } from './hooks/useSlideTitle';

export type CaseExplorerViewProps = Omit<CaseExplorerClientProps, 'forcedCurrSlide'> & {
  currSlideVariationDetails: UseSlideVariationReturnType;
  currentSlide: Slide | undefined;
  currentSlideWithVariation: Slide | null;
  slideIndex: number;
  isPreview: boolean;
  userID: string | undefined;
  currentHostAssetID: string | null | undefined;
  currentPatientCaseId: string | null;
};

const CaseExplorerView = ({
  patientCaseIndex,
  patientCase,
  meeting,
  hostFields,
  isHost,
  isPreview,
  currentSlide,
  currentSlideWithVariation,
  slideIndex,
  userID,
  currSlideVariationDetails,
  trackAssetSwitch,
  currentHostAssetID,
  currentPatientCaseId,
}: CaseExplorerViewProps) => {
  const { t } = useTranslation();
  const fromRight = keyframes`${fadeInLeft}`;

  const [modalAssets, setModalAssets] = useState<Asset[] | undefined>();
  const [_modalAssetIndex, setModalAssetIndex] = useState<number>(0);
  const [showPatientProfile, setShowPatientProfile] = useState<boolean>(false);
  const { isAdmin } = useLoggedInUserAuthContext();

  const anamenseItem = getAnamneseItemFromCase(patientCase);
  const shouldRenderLLXBackground = slideIndex < getIndexFirstSlideNotAnamnese(patientCase);
  const slideTitle = useSlideTitle();
  const slideShowStore = useSlideShowStore();

  const slideForLeftSidebar = useMemo(() => {
    if (currentSlideWithVariation?.type === SlideType.SLIDE_SHOW) {
      return slideShowStore.subSlides[slideShowStore.currentSubSlideIdx];
    }
    return currentSlideWithVariation;
  }, [currentSlideWithVariation, slideShowStore]);

  const pinchAnimationUniqueKey = createPinchingAnimationMeetingUniqueKey(
    meeting?.id || 'no-meeting',
    meeting?.startDate?.toString() ?? null,
    meeting?.isInTesting ?? false
  );

  // --------------------- handlers ------------------------

  const showMoreInfoAssets = (assetRefs: string[]) => {
    const assetList = getAssetListByRefOrThrow(assetRefs, patientCase.assets);
    setModalAssetIndex(0);
    void trackAssetSwitch?.({
      newAssetID: assetList[0].id,
      assetName: assetList[0].title,
      isLibraryAsset: false,
    });
    setModalAssets(assetList);
  };

  /*
  NOTE: If the slide changes and the modal assets are still open, we close them
  */

  useEffect(() => {
    // if (previousSlideIndex !== slideIndex) {
    setModalAssets(undefined);
    // }
  }, [slideIndex]);

  // --------------------- effect ------------------------

  /*
  Note: I think this code is useless but I'm not sure so I'm just commenting it before deleting it
  */

  // useEffect(() => {
  //   const filteredModalAssets = currentSlide?.items
  //     .filter((slide) => slide.assets && slide.assets?.length > 0)
  //     .filter((data) =>
  //       data.assets?.filter(
  //         (asset) =>
  //           modalAssets && modalAssets.some((el) => asset.url === el.url)
  //       )
  //     );

  //   if (filteredModalAssets) {
  //     setModalAssets(undefined);
  //   }
  // }, [currentSlide?.id]);

  // --------------------- render ------------------------

  if (!currentSlide || !slideForLeftSidebar) {
    // CASE EMPTY
    return (
      <Flex w="full" justifyContent="center">
        <Empty>{t('noSlideFound')}</Empty>
      </Flex>
    );
  }

  return (
    <Flex direction="column" height="100%" w="full" overflow="hidden" position="relative">
      <Flex justifyContent="space-between">
        <Flex flexDirection="column" padding="25px 20px 10px 60px" backgroundColor="white">
          <Heading
            color="livelinx.purple200"
            fontSize="1.5em"
            animation={`0.7s ${fromRight}`}
            userSelect="none"
          >
            <Trans>{slideTitle}</Trans>
          </Heading>

          <HStack justifyContent="space-between" marginBottom="5px">
            <Flex alignItems="center" gap="20px" marginTop="5px"></Flex>
          </HStack>
        </Flex>
        {slideForLeftSidebar.topRight && (
          <TopRightItem item={slideForLeftSidebar.topRight} slides={patientCase.slides} />
        )}
      </Flex>
      <Divider />
      <Flex
        flex="auto"
        pt="10px"
        backgroundSize="cover"
        flexDirection="column"
        position="relative"
        backgroundColor={shouldRenderLLXBackground ? 'livelinx.purple400' : 'white'}
        backgroundImage={shouldRenderLLXBackground ? liveMeetingBackground : ''}
      >
        {meeting?.isInTesting && <MeetingTestingModeWarning isAdmin={isAdmin} />}
        <CaseSlide
          key={currentSlide.id} // this wont be updated without this...
          // key={currentSlide!.id + '' + answers.length} // this wont be updated without this...
          isHost={isHost}
          caseIndex={patientCaseIndex}
          slide={currentSlide}
          slideIndex={slideIndex}
          onShowAssets={(assetList) => {
            showMoreInfoAssets(assetList);
          }}
          isInViewMode={isPreview}
          assetList={patientCase.assets}
          currentCase={patientCase}
          currentPatientCaseId={currentPatientCaseId}
          variationDetails={currSlideVariationDetails}
          currentHostAssetID={currentHostAssetID}
        />
      </Flex>
      {modalAssets && (
        <AssetModal
          assetList={modalAssets}
          onAssetChange={(oldAsset: Asset, newAsset: Asset) => {
            void trackAssetSwitch?.({
              newAssetID: newAsset.id,
              assetName: newAsset.path,
              isLibraryAsset: false,
            });
          }}
          onModalClose={() => {
            void trackAssetSwitch?.(null);
            setModalAssets(undefined);
          }}
          currentHostAssetID={hostFields?.currentAssetID}
          shouldShowHostAsset={meeting?.hostId !== userID}
          pinchAnimationUniqueKey={pinchAnimationUniqueKey}
        />
      )}
      {anamenseItem && (
        <SimpleContentModal
          isOpen={showPatientProfile}
          onClose={() => setShowPatientProfile(false)}
          content={
            <AnamneseItem
              title={`CASES ${patientCaseIndex + 1}`}
              isWomanCase={anamenseItem.gender === 'woman'}
              subtitle={anamenseItem.title}
              descriptionList={anamenseItem.descriptionList}
            />
          }
        />
      )}
      <CaseExplorerLeftSidebar
        patientCase={patientCase}
        currentSlide={slideForLeftSidebar}
        slideIndex={slideIndex}
        onPatientCaseClick={() => setShowPatientProfile(true)}
        onSupportingDocsClick={() => showMoreInfoAssets(slideForLeftSidebar.libraryRefs)}
      />
    </Flex>
  );
};

export default CaseExplorerView;
